import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FormControl, InputLabel, OutlinedInput, FormHelperText } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { v4 as uuidv4 } from "uuid";

import DisplayString from "@components/input/strings/DisplayString";

interface Props {
  value: string | number | null;
  message?: string;
  name: string;
  onBlur?: Function | null;
  onChange?: Function | null;
  multiline?: boolean;
  labelWidth?: number | null;
  type?: string;
  color?: string;
  disabled?: boolean;
  // revert_key?: string;
  width?: number;
  className?: string;
  change_time?: number;
}

/**
 * A functional component version of StringBox
 * Handles delayed event firing to avoid excessive updates
 */
export default function StringBox({
  value,
  message = "",
  name,
  onBlur = null,
  onChange = null,
  multiline = false,
  // labelWidth = null,
  type = "text",
  color = "",
  disabled = false,
  // revert_key = "",
  width = -1,
  className = "",
  change_time = 0
}: Props) {
  const [last_value, setLastValue] = useState<string>(value ? `${value}` : "");
  const [inputValue, setInputValue] = useState<string>(value ? `${value}` : "");
  const [do_blur, setDoBlur] = useState<boolean>(false);
  
  // Refs that persist between renders
  const trueId = useRef<string>(uuidv4().toString());
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (`${value}` !== last_value) {
      setLastValue(`${value}`);
      setInputValue(`${value}`);
    }
  }, [value, last_value, inputValue]);

  // Handle blur with string normalization
  const handleBlur = useCallback(() => {
    let new_value = inputValue.trim();
    while (new_value.includes("\t")) {
      new_value = new_value.replace("\t", " ");
    }
    while (new_value.includes("  ")) {
      new_value = new_value.replace("  ", " ");
    }
    if (type === "number") {
      const numberValue = +new_value;
      if (isNaN(numberValue)) {
        new_value = "0";
      } else {
        new_value = numberValue.toString();
      }
    }
   
    if (new_value !== inputValue) {
      setInputValue(new_value);
    }
   
    if (onBlur) {
      onBlur(new_value);
    } else if (onChange) {
      onChange(new_value);
    }
  }, [inputValue, onBlur, onChange, type]);

  useEffect(() => {
    if (do_blur) {
      setDoBlur(false);
      handleBlur();
    }
  }, [do_blur, handleBlur, setDoBlur]);

  // Handle change with debounced event firing
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
   
    // Update UI immediately
    setInputValue(newValue);
   
    // // Clear existing timeout
    // if (timeoutIdRef.current) {
    //   clearTimeout(timeoutIdRef.current);
    //   timeoutIdRef.current = null;
    // }
   
    // // Set new timeout for delayed event firing
    // timeoutIdRef.current = setTimeout(() => {
    //   // For numeric input, trigger onBlur
    //   if (type === "number" && onBlur) {
    //     setDoBlur(true);
    //   } else if (onChange) {
    //     setDoBlur(true);
    //   }
    // }, 1000);
   
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      // For numeric input, trigger onBlur
      if (type === "number" && onBlur) {
        setDoBlur(true);
      } else if (onChange) {
        setDoBlur(true);
      }
    }, 1000);
  };

  // Handle key events
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !multiline && onBlur) {
      setDoBlur(true);
    }
  };

  // Render the actual control based on component state
  const renderControl = () => {
    if (disabled) {
      if (multiline) {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              className={"header padded"}
              size={{
                xs: 12
              }}>
              {name}
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              <DisplayString str={inputValue} />
            </Grid>
          </Grid>
        );
      } else {
        return (
          <div>
            <span className={"header padded"}>
              {name}:
            </span>
            <span>
              {inputValue}
            </span>
          </div>
        );
      }
    } else {
      // console.log("inputValue", inputValue);
      const label = <InputLabel htmlFor={`stringInput_${trueId.current}`}>{name}</InputLabel>;
      const input =
        <OutlinedInput
          id={`stringInput_${trueId.current}`}
          name={`stringInput_${trueId.current}`}
          type={type}
          autoComplete="Off"
          value={inputValue}
          disabled={disabled}
          color="primary"
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          onBlur={() => {
            if (type !== "number" && onBlur) {
              setDoBlur(true);
            }
          }}
          // labelWidth is deprecated but included for backward compatibility
          fullWidth
          multiline={multiline}
        />;
      const helper =
        <FormHelperText>
          {message}
        </FormHelperText>;

      if (width === -1) {
        return (
          <FormControl variant="outlined" fullWidth>
            {label}
            {input}
            {helper}
          </FormControl>
        );
      } else {
        return (
          <FormControl variant="outlined" style={{ width }}>
            {label}
            {input}
            {helper}
          </FormControl>
        );
      }
    }
  };

  // Apply conditional styling and render
  if (color !== "") {
    const colorValue = color === "blue" ? "#1C9AEF" : color;
    return (
      <div style={{ border: `1px solid ${colorValue}` }} className={className}>
        {renderControl()}
      </div>
    );
  } else if (className !== "") {
    return (
      <div className={className}>
        {renderControl()}
      </div>
    );
  } else {
    return renderControl();
  }
}
